import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, Leaf, Star, Shield, Clock, Users, ArrowRight, MapPin } from 'lucide-react';

const useScrollAnimation = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

const ValueCard = ({ icon: Icon, title, desc }) => (
  <div className="text-center p-6 animate-on-scroll">
    <div className="bg-green-100 rounded-2xl p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
      <Icon size={28} className="text-green-600" />
    </div>
    <h3 className="font-bold text-gray-900 text-base mb-2">{title}</h3>
    <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
  </div>
);

export default function About() {
  useScrollAnimation();

  return (
    <div>
      {/* PAGE HEADER */}
      <section className="hero-gradient py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-green-400 font-bold text-sm uppercase tracking-widest">About Us</span>
          <h1 className="text-5xl font-black text-white mt-3 mb-4">About Lawlor Rubbish Removal</h1>
          <p className="text-green-200 text-lg max-w-2xl mx-auto">
            Locally owned and operated — serving the communities of Dublin, Meath, Louth and Kildare with honest, professional waste removal services.
          </p>
        </div>
      </section>

      {/* ABOUT SECTION */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-on-scroll">
              <span className="text-green-600 font-bold text-sm uppercase tracking-wider">Our Story</span>
              <h2 className="text-4xl font-black text-gray-900 mt-2 mb-6">
                Chris Lawlor — Man With a Van, Heart for the Community
              </h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                Lawlor Rubbish Removal was founded by <strong>Chris Lawlor</strong>, a hardworking local man who saw a need for a fast, affordable, and genuinely reliable rubbish removal service in the Dublin area and surrounding counties.
              </p>
              <p className="text-gray-600 leading-relaxed mb-5">
                What started as a simple "man with a van" operation has grown into a trusted household name across <strong>Dublin, Meath, Louth, and Kildare</strong>. Every job is handled personally with care, efficiency, and a commitment to leaving things cleaner than we found them.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                We believe that rubbish removal should be stress-free for our customers. That means honest pricing, same-day availability, and a team that actually shows up when they say they will. <strong>No job is too big or too small.</strong>
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  'Licensed Waste Carriers',
                  'Fully Insured',
                  'Same Day Service',
                  'Free Estimates',
                  'Weekend Availability',
                  'Eco-Friendly Disposal',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-gray-700 text-sm">
                    <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
              <a
                href="tel:0852730131"
                className="inline-flex items-center gap-3 bg-green-600 hover:bg-green-700 text-white font-bold px-7 py-4 rounded-2xl transition-colors"
              >
                <Phone size={20} />
                Call Chris: 085 273 0131
              </a>
            </div>

            <div className="animate-on-scroll">
              <div className="relative">
                <div className="bg-green-50 rounded-3xl p-8 border border-green-100">
                  <div className="bg-green-600 rounded-2xl p-6 mb-6 text-white text-center">
                    <div className="text-6xl font-black mb-2">C</div>
                    <div className="text-xl font-black">Chris Lawlor</div>
                    <div className="text-green-200 text-sm">Founder & Owner</div>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-white rounded-xl p-4 flex items-center gap-4 shadow-sm">
                      <MapPin size={20} className="text-green-600" />
                      <div>
                        <div className="font-bold text-gray-900 text-sm">Based in Dublin</div>
                        <div className="text-gray-500 text-xs">Serving the greater Leinster region</div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-4 flex items-center gap-4 shadow-sm">
                      <Clock size={20} className="text-green-600" />
                      <div>
                        <div className="font-bold text-gray-900 text-sm">7 Days a Week</div>
                        <div className="text-gray-500 text-xs">Mon–Sun, 7am–8pm including weekends</div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-4 flex items-center gap-4 shadow-sm">
                      <Star size={20} className="text-green-600" />
                      <div>
                        <div className="font-bold text-gray-900 text-sm">5-Star Rated</div>
                        <div className="text-gray-500 text-xs">Consistently 5-star reviews from customers</div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-4 flex items-center gap-4 shadow-sm">
                      <Shield size={20} className="text-green-600" />
                      <div>
                        <div className="font-bold text-gray-900 text-sm">Licensed & Insured</div>
                        <div className="text-gray-500 text-xs">Fully licensed waste carrier, fully insured</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 animate-on-scroll">
            <span className="text-green-600 font-bold text-sm uppercase tracking-wider">Our Values</span>
            <h2 className="text-4xl font-black text-gray-900 mt-2 mb-4">What We Stand For</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <ValueCard icon={Clock} title="Punctuality" desc="We show up on time, every time. Respect for your schedule is our top priority." />
            <ValueCard icon={Shield} title="Integrity" desc="Honest quotes, no hidden fees, and no surprises. What you're quoted is what you pay." />
            <ValueCard icon={Leaf} title="Sustainability" desc="We responsibly dispose of waste and recycle wherever possible to protect our environment." />
            <ValueCard icon={Users} title="Community" desc="Locally owned and proud to give back to the communities we serve every day." />
          </div>
        </div>
      </section>

      {/* AREAS SERVED */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 animate-on-scroll">
            <span className="text-green-600 font-bold text-sm uppercase tracking-wider">Coverage</span>
            <h2 className="text-4xl font-black text-gray-900 mt-2 mb-4">Areas We Serve</h2>
            <p className="text-gray-500 max-w-xl mx-auto">We cover the full breadth of Dublin and the surrounding counties — no area is too remote.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { county: 'Dublin', areas: ['Dublin City', 'North Dublin', 'South Dublin', 'West Dublin', 'Fingal', 'Dún Laoghaire'] },
              { county: 'Meath', areas: ['Navan', 'Drogheda', 'Trim', 'Kells', 'Dunshaughlin', 'Ashbourne'] },
              { county: 'Louth', areas: ['Dundalk', 'Drogheda', 'Ardee', 'Carlingford', 'Dunleer', 'Termonfeckin'] },
              { county: 'Kildare', areas: ['Naas', 'Newbridge', 'Maynooth', 'Kildare Town', 'Athy', 'Celbridge'] },
            ].map(({ county, areas }) => (
              <div key={county} className="bg-gray-50 rounded-2xl p-6 animate-on-scroll">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin size={18} className="text-green-600" />
                  <h3 className="font-black text-gray-900 text-lg">Co. {county}</h3>
                </div>
                <ul className="space-y-2">
                  {areas.map((area) => (
                    <li key={area} className="text-gray-500 text-sm flex items-center gap-2">
                      <span className="text-green-500 text-xs">●</span>
                      {area}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="hero-gradient py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-black text-white mb-4">Let's Work Together</h2>
          <p className="text-green-200 text-lg mb-8">
            Get in touch with Chris today for a fast, free, no-obligation quote on any job.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:0852730131"
              className="inline-flex items-center justify-center gap-3 bg-green-500 hover:bg-green-400 text-white font-black px-10 py-5 rounded-2xl transition-all text-xl"
            >
              <Phone size={24} />
              085 273 0131
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/40 text-white font-bold px-10 py-5 rounded-2xl transition-all text-lg"
            >
              Get a Free Quote <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
